'use client';

import { Link } from '@/components/shared/Link';
import { UploadedImage } from '@/components/shared/UploadedImage';
import { stripHtml } from '@/lib/utils';

interface PersonCardProps {
  slug: string;
  name: string;
  position: string;
  image?: string;
  variant?: 'dark' | 'light';
}

export function PersonCard({ slug, name, position, image, variant = 'light' }: PersonCardProps) {
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const isDark = variant === 'dark';

  return (
    <Link
      href={`/our-people/${slug}`}
      className={`group relative block overflow-hidden rounded-2xl transition-all duration-500 border ${
        isDark
          ? 'bg-navy/40 border-white/10 hover:bg-navy/60 shadow-lg'
          : 'bg-white border-gray-100 shadow-sm hover:shadow-xl'
      }`}
    >
      <div className="relative w-full aspect-[4/5] overflow-hidden bg-navy/5">
        {image ? (
          <UploadedImage
            src={image}
            alt={name}
            width={600}
            height={750}
            className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
          />
        ) : (
          <div
            className={`w-full h-full flex items-center justify-center ${isDark ? 'bg-white/5 text-white/20' : 'bg-navy/10 text-navy/30'}`}
          >
            <span className="text-4xl font-bold font-heading">{initials}</span>
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/20 to-transparent opacity-0 md:opacity-100 transition-opacity duration-500" />
        <div className="absolute inset-0 bg-primary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </div>

      <div className="p-5 md:absolute md:bottom-0 md:left-0 md:right-0 md:p-8 md:z-10 text-left transition-all duration-500">
        <h4
          className={`text-lg md:text-xl font-bold leading-tight font-heading transition-colors duration-300 ${
            isDark ? 'text-white group-hover:text-primary' : 'text-navy md:text-white group-hover:text-primary'
          }`}
        >
          {name}
        </h4>
        <p
          className={`text-[13px] md:text-sm mt-2 mb-4 font-sans font-medium line-clamp-1 transition-colors duration-300 ${
            isDark ? 'text-white/60' : 'text-gray-500 md:text-white/80'
          }`}
        >
          {stripHtml(position)}
        </p>
      </div>
    </Link>
  );
}
